/**
 * Числа и даты на шкалах графика — по-человечески.
 *
 * Библиотека по умолчанию пишет «87000.00»: машинная точка, лишние
 * нули, моноширинный шрифт — шкала выходит широкой и читается как
 * распечатка. Здесь цена пишется как в шапке терминала: «87 000»,
 * «83 441,2», «0,000052»; знаков после запятой ровно столько, сколько
 * нужно, чтобы соседние деления различались. Даты — «24 сен», «12:00».
 *
 * Время на графике — всемирное (UTC), как и подпись под графиком.
 */

/** Узкий неразрывный пробел между разрядами: «87 000» не рвётся и не расползается. */
const GROUP = ' ';

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_TITLE = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WEEKDAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/** Число с разрядами и запятой: 83441.2 → «83 441,2». */
export function formatNumber(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value < 0 ? '−' : '';
  const fixed = Math.abs(value).toFixed(Math.max(0, Math.min(12, decimals)));
  const [whole = '0', fraction] = fixed.split('.');
  const grouped = whole.length > 4 ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP) : whole;
  return `${sign}${grouped}${fraction ? `,${fraction}` : ''}`;
}

/**
 * Сколько знаков нужно цене, точность которой неизвестна.
 *
 * 78 745,1 и 0,00004312 одинаково читаемы только при разном округлении:
 * у мелкой цены знаки считаются от первой значащей цифры.
 */
export function digitsFor(value: number): number {
  const abs = Math.abs(value);
  if (abs === 0 || !Number.isFinite(abs)) return 2;
  if (abs >= 1000) return 1;
  if (abs >= 1) return 3;
  return Math.min(12, Math.ceil(-Math.log10(abs)) + 3);
}

/** Цена на метке курсора и последней цены: с точностью инструмента. */
export function formatPrice(value: number, digits: number | null): string {
  return formatNumber(value, digits ?? digitsFor(value));
}

/**
 * Точность открытой монеты — для делений её шкалы.
 *
 * Деления раньше писались коротко («0,055» вместо «0,055000»), чтобы
 * шкала стала уже. Уже она не стала, а цена на делениях потеряла знаки —
 * владелец попросил писать полную цену. Форматер делений у библиотеки
 * один на все панели, поэтому точность монеты применяется только к
 * делениям её же порядка: у RSI (0–100) и объёма они свои.
 */
let instrument: { digits: number; price: number } | null = null;

export function setInstrumentPrecision(digits: number | null, price: number | null): void {
  instrument = digits !== null && price !== null && price > 0 ? { digits, price } : null;
}

/**
 * Деления шкалы цены.
 *
 * Знаков — столько, сколько различает шаг между делениями: при шаге
 * 500 — ни одного («87 000»), при шаге 0,5 — один, при шаге 0,000002 —
 * шесть. Нули в конце, одинаковые у всех делений, не пишутся.
 */
export function formatTicks(prices: readonly number[]): string[] {
  const sorted = [...prices].filter(Number.isFinite).sort((a, b) => a - b);
  let step = Number.POSITIVE_INFINITY;
  for (let i = 1; i < sorted.length; i += 1) {
    const diff = sorted[i]! - sorted[i - 1]!;
    if (diff > 0 && diff < step) step = diff;
  }
  let decimals: number;
  if (Number.isFinite(step)) {
    decimals = Math.max(0, Math.ceil(-Math.log10(step) - 1e-9));
    // Шаг вроде 0,25 требует двух знаков, а не одного.
    while (decimals < 12 && Math.abs(Math.round(step * 10 ** decimals) - step * 10 ** decimals) > 1e-6) decimals += 1;
  } else {
    decimals = sorted[0] !== undefined ? digitsFor(sorted[0]) : 2;
  }
  const middle = sorted[Math.floor(sorted.length / 2)];
  if (instrument && middle !== undefined && middle > instrument.price / 50 && middle < instrument.price * 50) {
    decimals = Math.max(decimals, instrument.digits);
  }
  return prices.map((price) => formatNumber(price, decimals));
}

/**
 * Значение индикатора на шкале — коротко.
 *
 * Ширина шкалы у библиотеки общая для всех панелей и равна самой
 * длинной подписи на любой из них. Одна подпись индикатора в формате
 * «83766.00» раздувала шкалу всего графика, хотя деления «87 000» узкие.
 * Знаков — по величине: 83 766 · 555,3 · 55,30 · 0,00012.
 */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 0 : abs >= 100 ? 1 : abs >= 1 || abs === 0 ? 2 : Math.min(8, Math.ceil(-Math.log10(abs)) + 1);
  return formatNumber(value, digits);
}

/** Формат ряда индикатора: компактная подпись и мелкий шаг — чтобы деления мелких значений различались. */
export const COMPACT_FORMAT = { type: 'custom' as const, minMove: 1e-8, formatter: formatCompact };

/** Вид метки на шкале времени — как у библиотеки (TickMarkType). */
export const TICK = { year: 0, month: 1, day: 2, time: 3, seconds: 4 } as const;

const pad = (value: number): string => String(value).padStart(2, '0');

/** Метка на шкале времени: «2026», «Сентябрь», «24 сен», «12:00». */
export function formatTick(seconds: number, type: number): string {
  const date = new Date(seconds * 1000);
  switch (type) {
    case TICK.year:
      return String(date.getUTCFullYear());
    case TICK.month:
      return MONTHS_TITLE[date.getUTCMonth()]!;
    case TICK.day:
      return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
    case TICK.seconds:
      return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
    default:
      return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  }
}

/** Время под курсором: «Чт 24 сен 2026 · 12:00». */
export function formatCrosshairTime(seconds: number): string {
  const date = new Date(seconds * 1000);
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()} · ${pad(
    date.getUTCHours(),
  )}:${pad(date.getUTCMinutes())}`;
}
